import React, { useState, useEffect } from 'react';
import { NavLink, useSearchParams } from "react-router-dom";
import styled from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import LoggedLayout from "layouts/LoggedLayout";
import Paginator from 'components/Paginator';
import ThSortable from "components/ThSortable";
import TrSkeleton from "components/TrSkeleton";
import { authUserPermission } from 'helpers/community';
import BankAggregatorLogModal from 'components/BankAggregatorLogModal';

const Table = styled.table`
	th,td {
		&:nth-child(1) {
			
		}

		&:nth-child(2) {
			width: 80px;
		}

		&:nth-child(3) {
			width: 150px;
		}

		&:nth-child(4) {
			width: 40px;
		}
	}
`;

let axiosCancelToken = null;
let searchTimeout = null;

export default function BankContract() {
	const [queryParams] = useSearchParams();

	let [bankContracts, setBankContracts] = useState({});
	let [search, setSearch] = useState(undefined);
	let [sortDirection, setSortDirection] = useState('asc');
	let [sortField, setSortField] = useState('name');
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? undefined);
	const setPage = (page) => {
		setBankContracts({...bankContracts, data: undefined});
		_setPage(page);
	}
	let [bankAggregatorLogModalOpened, setBankAggregatorLogModalOpened] = useState(null);

	const setSearchTimeout = (value) => {
		if ( searchTimeout ) clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => {
			setSearch(value);
			setPage(1);
		}, 1000);
	}

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		const getBankContracts = async () => {
			setBankContracts({});

			await axios.get('/api/manager/bank-contracts/list', {
				params: {
					sort: sortField,
					direction: sortDirection,
					page: page,
					search: search
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
				setBankContracts({...response.data});
				setSkeletonRows(response.data?.data?.length);
			}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});
		}
		getBankContracts();
	}, [page, search, sortField, sortDirection]);

	return (
		<LoggedLayout>
			<section>
				<div className="page-title">
					<h1>Contratos bancarios</h1>
					
					{ authUserPermission('add') &&
						<NavLink to="/bank-contracts/add" className="btn btn-sm btn-light ms-auto">Nuevo contrato</NavLink>
					}
				</div>

				<div className="page-content">
					<div className="card border-0 shadow-sm">
						<div className="card-header bg-white p-3">
							<div className="row">
								<div className="col-md-3 mb-2 mt-2 mb-md-0 mt-md-0">
									<input type="search" className="form-control form-control-sm" placeholder="Buscar" onChange={(e) => setSearchTimeout(e.target.value)} />
								</div>
								<div className="col-md-2 mb-2 mt-2 mb-md-0 mt-md-0">
								</div>
								<div className="col-md-4 mb-2 mt-2 mb-md-0 mt-md-0">
								</div>
							</div>
						</div>
						<div className="card-body p-0">
							<div className="table-responsive table-responsive-carded">
								<Table className="table table-hover table-sortable table-carded">
									<thead>
										<tr>
											<ThSortable direction={sortDirection} active={sortField === 'name'} onClick={() => sortTableClick('name')}>Nombre</ThSortable>
											<th>Sincronización</th>
											<th>Última sincronización</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{ bankContracts.data?.length > 0 &&
											bankContracts.data.map((el, idx) => {
												return ( 
													<React.Fragment key={idx}>
														<tr>
															<td>
																{el.name ?? '-'}
																{ el.default === 1 && 
																	<div><small><i className="bi bi-asterisk text-primary"></i> Por defecto</small></div>
																}
															</td>
															<td>
																{el.sync_status === 1 &&
																	<span className="badge bg-success">Activa</span>
																}

																{el.sync_status === 0 &&
																	<span className="badge bg-danger">Inactiva</span>
																}
															</td>
															<td>
																<button className="btn btn-link btn-sm p-0 me-2" onClick={() => setBankAggregatorLogModalOpened(el)}><i className="bi bi-eye"></i></button>	
																{el.synced_at ? moment(el.synced_at).format('DD-MM-YYYY HH:mm') : ''}
															</td>
															<td className="text-end">
																<NavLink className="btn-table" to={'/bank-contracts/edit/' + el.id}><i className="bi bi-pencil"></i></NavLink>
															</td>
														</tr>
													</React.Fragment>
												);
											})
										}

										{ (Object.keys(bankContracts)?.length > 0 && !bankContracts.data?.length) && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

										{ Object.keys(bankContracts)?.length === 0 && <TrSkeleton rows={skeletonRows} columns={5} /> }
									</tbody>
								</Table>
							</div>
						</div>
						<div className="card-footer p-3 d-flex justify-content-end">
							<div className="d-inline-block">
								<Paginator
									min={1}
									current={bankContracts?.current_page}
									max={bankContracts?.last_page}
									changeCallback={(page) => setPage(page)}
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{bankAggregatorLogModalOpened &&
				<BankAggregatorLogModal 
					bankcontract={bankAggregatorLogModalOpened}
					closeCallback={() => {
						setBankAggregatorLogModalOpened(false);
					}}
				/>
			}
		</LoggedLayout>
	);
}


